/* X1 North American Demo Tour — shared form runtime.
 *
 * Drives all four forms. The payload contract is unchanged from the original
 * single-page build: same endpoints, same field names, same value types, one
 * POST per form. Only when and how the questions are asked has changed.
 */
(function () {
  'use strict';

  var BOOLEAN_FIELDS = [
    'guardianConfirmed',
    'consentFollowUp',
    'waiverAcknowledged',
    'photoConsent',
    'repostPermission'
  ];

  var MAX_PRIORITIES = 3;

  function stopStartMinutes(stop) {
    var match = String(stop && stop.time || '').match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);
    if (!match) return 24 * 60;
    var hour = Number(match[1]) % 12;
    if (match[3].toUpperCase() === 'PM') hour += 12;
    return hour * 60 + Number(match[2] || 0);
  }

  // Compare wall-clock times in the event's zone, never the visitor's zone.
  function stopStatus(stop, now) {
    var parts = {};
    try {
      new Intl.DateTimeFormat('en-US', {
        timeZone: stop.timezone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(now || new Date()).forEach(function (part) { parts[part.type] = part.value; });
      if (!stop.timezone) return 'unknown';
    } catch (error) { return 'unknown'; }
    var date = parts.year + '-' + parts.month + '-' + parts.day;
    var endDate = stop.endDate || stop.date;
    if (date < stop.date) return 'upcoming';
    if (date > endDate) return 'completed';
    var times = String(stop.time || '').match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/gi) || [];
    // Without both bounds, do not claim that a demo is currently running.
    if (times.length !== 2) return 'today';
    var start = stopStartMinutes({ time: times[0] });
    var end = stopStartMinutes({ time: times[1] });
    var minute = Number(parts.hour) * 60 + Number(parts.minute);
    if (date === stop.date && minute < start) return 'upcoming';
    if (date === endDate && minute >= end) return 'completed';
    return 'live';
  }

  function compareStopsChronologically(a, b) {
    return String(a.date || '').localeCompare(String(b.date || ''))
      || stopStartMinutes(a) - stopStartMinutes(b)
      || String(a.id || '').localeCompare(String(b.id || ''));
  }

  function postJson(apiBase, path, body) {
    return fetch(apiBase + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        if (!response.ok) {
          var error = new Error(data.error || 'The request could not be completed.');
          error.code = data.code || '';
          throw error;
        }
        return data;
      });
    });
  }

  function milesBetween(a, b) {
    var toRadians = Math.PI / 180;
    var lat1 = Number(a.lat) * toRadians, lat2 = Number(b.lat) * toRadians;
    var dLat = lat2 - lat1, dLon = (Number(b.lon) - Number(a.lon)) * toRadians;
    var h = Math.sin(dLat / 2) ** 2
      + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 3958.8 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }

  function surveyReviewMode() {
    try { return new URLSearchParams(window.location.search).get('review') === 'survey'; }
    catch (error) { return false; }
  }

  function init(root) {
    if (!root || root.dataset.x1Ready === 'true') return;
    root.dataset.x1Ready = 'true';

    var apiBase = (root.getAttribute('data-api-url') || '').replace(/\/+$/, '');
    var form = root.querySelector('[data-x1-form]');
    if (!form) return;

    var kind = form.dataset.kind;

    /* ---------------------------------------------------------------- utils */

    /* The unlock step has its own message target: the main one lives inside the
       survey, which is still hidden while the unlock step is on screen. */
    function message(state, text, target) {
      var el = form.querySelector(target === 'unlock' ? '[data-x1-unlock-msg]' : '[data-x1-msg]');
      if (!el) return;
      el.dataset.state = state || '';
      el.textContent = text || '';
    }

    function payload() {
      var data = new FormData(form);
      var out = {};

      data.forEach(function (value, key) {
        if (key === 'nextWheelPriorities') return;
        if (BOOLEAN_FIELDS.indexOf(key) !== -1) return;
        out[key] = value;
      });

      BOOLEAN_FIELDS.forEach(function (name) {
        if (form.querySelector('[name="' + name + '"]')) out[name] = data.has(name);
      });

      if (form.querySelector('[name="nextWheelPriorities"]')) {
        out.nextWheelPriorities = data.getAll('nextWheelPriorities').join(' | ');
      }

      return out;
    }

    function request(path, body) {
      return postJson(apiBase, path, body);
    }

    /* ------------------------------------------------ register: guardian ---- */

    var age = form.querySelector('[data-x1-age]');
    var guardian = form.querySelector('[data-x1-guardian]');

    function syncGuardian() {
      if (!age || !guardian) return;
      var minor = age.value === 'Under 21';
      guardian.hidden = !minor;
      guardian.querySelectorAll('input').forEach(function (input) {
        input.required = minor;
      });
    }

    if (age) {
      age.addEventListener('change', syncGuardian);
      syncGuardian();
    }

    /* ------------------------------------------- the confirmed schedule ---- */

    /* Stops come from the Operations workbook via /api/events — the same feed
       the hub page reads — so a time or venue corrected in the workbook is
       correct here without a theme deploy. Nothing about the schedule is
       hardcoded in the theme. */

    var eventSelect = form.querySelector('[data-x1-event-select]');

    function requestedEventId() {
      try {
        return new URLSearchParams(window.location.search).get('event');
      } catch (error) {
        return null;
      }
    }

    function todayStamp() {
      var d = new Date();
      return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }

    function stampOf(stop) {
      return Number(String(stop.date || '').replace(/-/g, '')) || 0;
    }

    function fillEvents(stops) {
      if (!eventSelect) return;

      // Registration is only meaningful for a stop that has not happened yet.
      var usable = eventSelect.dataset.x1EventSelect === 'past-ok'
        ? stops
        : stops.filter(function (s) { return stopStatus(s) !== 'completed'; });

      // A form whose only purpose is picking a stop has nothing to offer once
      // every stop has passed, so it says so rather than showing an empty menu.
      if (!usable.length && eventSelect.dataset.x1EventSelect !== 'past-ok') {
        var closed = root.querySelector('[data-x1-no-events]');
        if (closed) {
          // Hide the whole form view, not just the <form>: its intro heading
          // would otherwise sit above the closed notice contradicting it.
          var view = root.querySelector('[data-x1-form-view]') || form;
          view.hidden = true;
          closed.hidden = false;
        }
        return;
      }

      var keep = [].slice.call(eventSelect.querySelectorAll('option[data-x1-keep]'));
      eventSelect.innerHTML = '';
      keep.forEach(function (o) {
        // The placeholder ships as "Loading…" so a slow feed reads honestly;
        // once stops are in it has to stop claiming to be loading.
        if (o.value === '' && o.dataset.x1Loaded !== 'true') {
          o.textContent = 'Choose a stop';
          o.dataset.x1Loaded = 'true';
        }
        eventSelect.appendChild(o);
      });

      usable.forEach(function (stop) {
        var o = document.createElement('option');
        o.value = stop.id;
        o.textContent = (stop.dateLabel || stop.date) + ' — ' + (stop.title || stop.city);
        eventSelect.appendChild(o);
      });

      // Resolve ?event=EVT-06, the link behind every per-stop QR code. An
      // unknown or stale id falls through to the placeholder rather than
      // silently registering someone for the wrong stop.
      var wanted = requestedEventId();
      if (wanted && [].slice.call(eventSelect.options).some(function (o) { return o.value === wanted; })) {
        eventSelect.value = wanted;
      }
    }

    function eventsUnavailable(text) {
      if (!eventSelect) return;
      var keep = [].slice.call(eventSelect.querySelectorAll('option[data-x1-keep]'));
      eventSelect.innerHTML = '';
      keep.forEach(function (o) { eventSelect.appendChild(o); });
      var o = document.createElement('option');
      o.value = '';
      o.disabled = true;
      o.textContent = text;
      eventSelect.appendChild(o);
    }

    if (eventSelect) {
      fetch(apiBase + '/api/events', { headers: { accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('Schedule feed unavailable');
          return r.json();
        })
        .then(function (data) {
          if (!data || data.ok !== true || !Array.isArray(data.stops)) throw new Error('Schedule feed unavailable');
          var stops = data.stops.slice().sort(compareStopsChronologically);
          if (!stops.length) { eventsUnavailable('No confirmed stops listed yet'); return; }
          fillEvents(stops);
        })
        .catch(function () {
          eventsUnavailable('Schedule temporarily unavailable — please try again shortly');
        });
    }

    /* ------------------------------------------- feedback: unlock gate ------ */

    var unlock = form.querySelector('[data-x1-unlock]');
    var survey = form.querySelector('[data-x1-survey]');
    var reviewOnly = kind === 'post-ride' && surveyReviewMode();

    if (reviewOnly && survey) {
      var unlockGroup = form.querySelector('[data-x1-unlock-group]');
      var reviewNotice = form.querySelector('[data-x1-review-notice]');
      var reviewSubmit = form.querySelector('[type="submit"]');
      if (unlockGroup) unlockGroup.hidden = true;
      if (reviewNotice) reviewNotice.hidden = false;
      survey.hidden = false;
      if (reviewSubmit) {
        reviewSubmit.disabled = true;
        reviewSubmit.textContent = 'Preview only — submission disabled';
      }
    }

    if (unlock && survey && !reviewOnly) {
      unlock.addEventListener('click', function () {
        var fields = [].slice.call(unlock.closest('[data-x1-unlock-group]').querySelectorAll('input'));
        var missing = fields.filter(function (input) { return !input.checkValidity(); });

        if (missing.length) {
          missing.forEach(function (input) { input.setAttribute('aria-invalid', 'true'); });
          message('error', 'Enter the rider full legal name and email used on the waiver to continue.', 'unlock');
          missing[0].focus();
          return;
        }

        fields.forEach(function (input) { input.removeAttribute('aria-invalid'); });
        unlock.disabled = true;
        message('', 'Checking your signed waiver…', 'unlock');
        var body = payload();
        request(form.dataset.endpoint, { fullName: body.fullName, email: body.email, lookupOnly: true })
          .then(function () {
            message('', '', 'unlock');
            survey.hidden = false;
            unlock.hidden = true;
            var firstLabel = survey.querySelector('.x1__field-label, label');
            if (firstLabel) firstLabel.scrollIntoView({ behavior: 'smooth', block: 'center' });
          })
          .catch(function (error) {
            message('error', error.message || 'We could not match your signed waiver.', 'unlock');
            showGate(error.code || '');
          })
          .finally(function () { unlock.disabled = false; });
      });
    }

    /* ------------------------------------- feedback: cap priority picks ----- */

    var priorities = [].slice.call(form.querySelectorAll('[name="nextWheelPriorities"]'));

    if (priorities.length) {
      priorities.forEach(function (box) {
        box.addEventListener('change', function () {
          var checked = priorities.filter(function (p) { return p.checked; });
          var atLimit = checked.length >= MAX_PRIORITIES;

          priorities.forEach(function (p) {
            if (!p.checked) p.disabled = atLimit;
          });

          var counter = form.querySelector('[data-x1-priority-count]');
          if (counter) {
            counter.textContent = atLimit
              ? 'Three selected — that is the maximum.'
              : checked.length + ' of ' + MAX_PRIORITIES + ' selected.';
          }
        });
      });
    }

    /* ------------------------------------------------ host: route estimate -- */

    var estimate = null;
    var estimateBox = root.querySelector('[data-x1-estimate]');

    function escapeText(value) {
      var node = document.createElement('div');
      node.textContent = value == null ? '' : String(value);
      return node.innerHTML;
    }

    function renderEstimate(state, data) {
      if (!estimateBox) return;

      if (state === 'idle') {
        estimateBox.innerHTML =
          '<strong>Route fit</strong>Add your venue address above and we will check it against the working route. ' +
          'Suggested windows are estimates, not reservations.';
        return;
      }

      if (state === 'loading') {
        estimateBox.innerHTML = '<strong>Checking the working route…</strong>One moment.';
        return;
      }

      if (state === 'error') {
        estimateBox.innerHTML =
          '<strong>We could not place that address</strong>' + escapeText(data) +
          ' Check the street, city, state and ZIP and try again. If it still will not resolve, ' +
          '<a href="' + escapeText(root.getAttribute('data-contact-url') || '/pages/contact') + '">send us the details directly</a>' +
          ' and we will assess the location by hand.';
        return;
      }

      // When the estimator cannot place an address it answers with
      // needsManualReview, extraHours null and windowStart/End "TBD". Rendering
      // that through the normal path claimed "about 0 hours" and offered "TBD
      // to TBD" as a date window, so the two cases are kept separate.
      //
      // The API's own `warning` string is deliberately not echoed: it names
      // individual staff, and this is a public page.
      if (data.needsManualReview) {
        estimateBox.innerHTML =
          '<strong>We will place this one by hand</strong>' +
          'We could not match that address automatically, which is often just an ' +
          'unusual or rural address. You can still submit — the tour team will ' +
          'review the location and come back to you.';
        return;
      }

      var hours = Number(data.extraHours);

      // The estimate is advisory — the route team decides, not this number. It
      // is presented as information rather than a verdict so a discouraging
      // result does not talk someone out of applying. That matters concretely
      // right now: the estimator's route has no Canadian waypoints, so every
      // BC address comes back "Unavailable for this tour" even though the tour
      // opens with five BC stops.
      estimateBox.innerHTML =
        '<strong>' + escapeText(data.fit || 'Manual review') + '</strong>' +
        'Suggested window: ' + escapeText(data.windowStart || '—') + ' to ' + escapeText(data.windowEnd || '—') +
        '<br>Closest leg of the route: ' + escapeText(data.between || '—') +
        (Number.isFinite(hours)
          ? '<br>Extra drive time: about ' + escapeText(hours) + (hours === 1 ? ' hour.' : ' hours.')
          : '') +
        '<br><br>This is an automatic estimate, not a decision — send it anyway ' +
        'if you think your location works and the tour team will take a look.';
    }

    function runEstimate() {
      var field = form.querySelector('[name="venueAddress"]');
      if (!field) return Promise.resolve(null);

      var address = field.value.trim();
      if (!address) {
        var err = new Error('Enter the full venue address, including city, state and ZIP.');
        message('error', err.message);
        field.focus();
        return Promise.reject(err);
      }

      renderEstimate('loading');

      return request('/api/estimate', { address: address }).then(function (data) {
        estimate = data;
        renderEstimate('ok', data);
        message('', '');
        return data;
      }).catch(function (error) {
        estimate = null;
        renderEstimate('error', error.message);
        throw error;
      });
    }

    var estimateButton = root.querySelector('[data-x1-estimate-button]');
    if (estimateButton) {
      estimateButton.addEventListener('click', function () {
        runEstimate().catch(function () { /* surfaced in the estimate box */ });
      });
    }

    var venue = form.querySelector('[name="venueAddress"]');
    if (venue) {
      venue.addEventListener('change', function () {
        estimate = null;
        renderEstimate('idle');
      });
    }

    /* ---------------------------------------------------- gate messaging --- */

    function showGate(code) {
      var gate = form.querySelector('[data-x1-gate]');
      if (!gate) return;

      var known = ['REGISTRATION_REQUIRED', 'WAIVER_REQUIRED', 'CHECKIN_REQUIRED'];
      gate.hidden = known.indexOf(code) === -1;

      gate.querySelectorAll('[data-x1-gate-for]').forEach(function (el) {
        el.hidden = el.getAttribute('data-x1-gate-for') !== code;
      });
    }

    /* ------------------------------------------------------------ submit --- */

    function succeed(data) {
      var done = root.querySelector('[data-x1-done]');
      if (!done) {
        message('success', 'Saved. Reference ' + data.id + '.');
        return;
      }

      var ref = done.querySelector('[data-x1-done-ref]');
      if (ref) ref.textContent = data.id || '—';

      form.hidden = true;
      done.hidden = false;
      done.setAttribute('tabindex', '-1');
      done.focus();
      done.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (reviewOnly) { message('error', 'This is a non-submitting survey preview. No response was recorded.'); return; }
      showGate('');

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      var button = form.querySelector('[type="submit"]');
      button.disabled = true;
      message('', 'Saving…');

      // The host endpoint requires the estimator's output, so a successful
      // route estimate is a precondition for submitting, not a nicety. An
      // earlier version swallowed estimate failures and posted anyway, which
      // the server rejected with a 400 the user could do nothing about.
      var prepare = (kind === 'host' && !estimate) ? runEstimate() : Promise.resolve(estimate);

      prepare.then(function (routeEstimate) {
        if (kind === 'host' && !routeEstimate) {
          throw new Error('Check the route fit before submitting. If your address will not resolve, contact us directly using the link above and we will assess it by hand.');
        }
        return routeEstimate;
      }).then(function (routeEstimate) {
        var body = payload();

        if (kind === 'host' && routeEstimate) {
          Object.keys(routeEstimate).forEach(function (key) { body[key] = routeEstimate[key]; });
          body.routeFit = routeEstimate.fit;
        }

        return request(form.dataset.endpoint, body);
      }).then(succeed).catch(function (error) {
        message('error', error.message || 'Something went wrong. Please try again.');
        showGate(error.code || '');
      }).finally(function () {
        button.disabled = false;
      });
    });

    renderEstimate('idle');
  }


  /* ==========================================================================
     Hub page: the confirmed schedule and the tour map.

     Stops come from the Operations workbook via /api/events, same as the form
     pages. Nothing about the schedule is hardcoded in the theme, so a corrected
     time or venue is live without a deploy.
     ========================================================================== */

  function initHub(root) {
    if (root.dataset.x1HubReady === 'true') return;
    root.dataset.x1HubReady = 'true';

    var apiBase = (root.getAttribute('data-api-url') || '').replace(/\/+$/, '');
    var rootUrl = root.getAttribute('data-root-url') || '/';

    var scheduleEl = root.querySelector('[data-x1-schedule]');
    var mapEl = root.querySelector('.x1__map');
    var rangeEl = root.querySelector('[data-x1-range]');
    var remainingEl = root.querySelector('[data-x1-remaining]');
    var countEl = root.querySelector('[data-x1-count]');
    var finder = root.querySelector('[data-x1-finder]');
    var finderInput = root.querySelector('[data-x1-finder-input]');
    var finderSubmit = root.querySelector('[data-x1-finder-submit]');
    var finderReset = root.querySelector('[data-x1-finder-reset]');
    var finderStatus = root.querySelector('[data-x1-finder-status]');
    var tentativeEl = root.querySelector('[data-x1-tentative]');
    var tentativeList = root.querySelector('[data-x1-tentative-list]');
    var allStops = [], nearestPoint = null;
    var tourMap = null, tourLayer = null, mapHasFit = false, mapPoints = [], leafletRetries = 0;

    var videoDialog = root.querySelector('[data-x1-video-dialog]');
    var videoOpen = root.querySelector('[data-x1-video-open]');
    var videoFrame = root.querySelector('[data-x1-video-dialog-frame]');

    if (videoDialog && videoOpen && videoFrame) {
      videoOpen.addEventListener('click', function () {
        videoFrame.src = videoFrame.getAttribute('data-src');
        videoDialog.showModal();
      });
      videoDialog.addEventListener('close', function () {
        videoFrame.removeAttribute('src');
      });
      videoDialog.addEventListener('click', function (event) {
        if (event.target === videoDialog) videoDialog.close();
      });
    }

    function fitMap() {
      if (!tourMap || !mapPoints.length || !mapEl.offsetWidth || !mapEl.offsetHeight) return;
      tourMap.invalidateSize();
      tourMap.fitBounds(mapPoints, { padding: [48, 48], maxZoom: 6 });
      mapHasFit = true;
    }

    function esc(v) {
      var n = document.createElement('div');
      n.textContent = v == null ? '' : String(v);
      return n.innerHTML;
    }

    function todayStamp() {
      var d = new Date();
      return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
    function stampOf(v) { return Number(String(v || '').replace(/-/g, '')) || 0; }

    /* A stop must carry everything the card renders; a half-populated workbook
       row is treated as unusable rather than rendered with blanks. */
    function validStop(stop) {
      if (!stop || typeof stop !== 'object') return false;
      return ['id', 'date', 'dateLabel', 'month', 'day', 'title', 'location', 'time', 'description', 'hostedBy']
        .every(function (k) { return typeof stop[k] === 'string' && stop[k].trim().length > 0; })
        && typeof stop.address === 'string';
    }

    function renderMap(stops) {
      if (!mapEl) return;
      if (!window.L) {
        if (leafletRetries < 20) {
          leafletRetries += 1;
          window.setTimeout(function () { renderMap(stops); }, 250);
        }
        return;
      }
      leafletRetries = 0;

      var plotted = stops.map(function (stop, index) {
        var lat = Number(stop.lat), lon = Number(stop.lon);
        return stop.lat != null && stop.lon != null && Number.isFinite(lat) && Number.isFinite(lon)
          ? { stop: stop, number: index + 1, point: [lat, lon] }
          : null;
      }).filter(Boolean);
      if (!plotted.length) return;

      if (!tourMap) {
        mapEl.replaceChildren();
        tourMap = L.map(mapEl);
        tourMap.attributionControl.setPrefix(false);
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19
        }).addTo(tourMap);
        tourLayer = L.layerGroup().addTo(tourMap);
        if (window.ResizeObserver) {
          new ResizeObserver(function () {
            tourMap.invalidateSize();
            if (!mapHasFit) fitMap();
          }).observe(mapEl);
        }
      }

      tourLayer.clearLayers();
      mapPoints = plotted.map(function (item) { return item.point; });
      L.polyline(mapPoints, { color: '#ff5a00', weight: 3, opacity: 0.9, dashArray: '8 8' }).addTo(tourLayer);
      plotted.forEach(function (item) {
        var stop = item.stop;
        var icon = L.divIcon({
          className: 'x1__map-marker',
          html: '<span aria-hidden="true">' + item.number + '</span>',
          iconSize: [40, 40],
          iconAnchor: [20, 20]
        });
        L.marker(item.point, {
          icon: icon,
          keyboard: true,
          title: 'Stop ' + item.number + ': ' + stop.title,
          alt: 'Stop ' + item.number + ': ' + stop.title
        }).bindPopup(
          '<strong>Stop ' + item.number + ' · ' + esc(stop.title) + '</strong><br>' +
          esc(stop.dateLabel) + ' · ' + esc(stop.time) + '<br>' +
          '<a href="#stop-' + esc(String(stop.id).toLowerCase()) + '">View schedule details</a>'
        ).addTo(tourLayer);
      });

      if (!mapHasFit) fitMap();
    }

    function cardFor(stop, isNext, distance) {
      var status = stopStatus(stop);
      var past = status === 'completed', live = status === 'live';
      var loc = [stop.location, stop.address].filter(Boolean).join(', ');

      var where = stop.mapUrl && loc
        ? '<a href="' + esc(stop.mapUrl) + '" target="_blank" rel="noopener">' + esc(loc) +
          '<span class="visually-hidden"> (opens in Google Maps)</span></a>'
        : esc(loc || 'Venue to be announced');

      // No public post-ride link: that form is reached only by QR at the event.
      var routeStatus = isNext ? '<p class="x1__stop-status">Next stop · ' + esc(stop.dateLabel) + '</p>' : '';
      var cta = live
        ? '<span class="x1__tag">Happening now</span>'
        : status === 'today'
          ? '<span class="x1__tag">Today</span>'
        : past
          ? '<span class="x1__tag x1__tag--done">Completed</span>'
          : '<a class="x1__btn x1__btn--primary" href="' + esc(rootUrl) + 'pages/x1-register?event=' +
            encodeURIComponent(stop.id) + '">Register interest<span class="visually-hidden"> for ' +
            esc(stop.title) + ' on ' + esc(stop.dateLabel) + '</span></a>';

      return '<article class="x1__stop-card' + (past ? ' x1__stop-card--done' : '') +
        (live ? ' x1__stop-card--live' : isNext ? ' x1__stop-card--next' : '') +
        '" id="stop-' + esc(String(stop.id).toLowerCase()) + '">' +
        '<div class="x1__stop-date"><span>' + esc(stop.month) + '</span><b>' + esc(stop.day) + '</b></div>' +
        '<div class="x1__stop-body">' +
          routeStatus +
          (distance ? '<p class="x1__stop-distance">' + esc(distance) + '</p>' : '') +
          '<h3>' + esc(stop.title) + '</h3>' +
          '<p class="x1__stop-name">' + esc(stop.description) + '</p>' +
          '<dl class="x1__stop-meta">' +
            '<div><dt>Time</dt><dd>' + esc(stop.time) + '</dd></div>' +
            '<div><dt>Where</dt><dd>' + where + '</dd></div>' +
            '<div><dt>Host</dt><dd>' + esc(stop.hostedBy) + '</dd></div>' +
          '</dl>' +
          '<div class="x1__stop-cta">' + cta + '</div>' +
        '</div></article>';
    }

    function nextStopId(stops) {
      var t = todayStamp();
      for (var i = 0; i < stops.length; i++) {
        if (stopStatus(stops[i]) === 'upcoming') return stops[i].id;
      }
      return null;
    }

    function renderSchedule(stops, distances) {
      if (!scheduleEl) return;
      var nextId = nextStopId(allStops);
      var previousDrawer = scheduleEl.querySelector('[data-x1-completed]');
      var expanded = previousDrawer && previousDrawer.open;
      var upcoming = [], completed = [];
      stops.forEach(function (stop) {
        (stopStatus(stop) === 'completed' ? completed : upcoming).push(stop);
      });
      function cards(items) {
        return items.map(function (stop) {
          return cardFor(stop, stop.id === nextId, distances && distances[stop.id]);
        }).join('');
      }
      scheduleEl.innerHTML = cards(upcoming) + (completed.length
        ? '<details class="x1__completed" data-x1-completed' + (expanded ? ' open' : '') + '>' +
          '<summary>Completed stops <span>(' + completed.length + ')</span></summary>' +
          '<div class="x1__stops x1__completed-list">' + cards(completed) + '</div></details>'
        : '');
      revealLinkedStop();
    }

    // Map links and shared stop links can still reach a collapsed past stop.
    function revealLinkedStop() {
      var id = window.location.hash.slice(1);
      if (!id || !scheduleEl) return;
      var target = document.getElementById(id);
      if (!target || !scheduleEl.contains(target)) return;
      var drawer = target.closest('[data-x1-completed]');
      if (drawer && !drawer.open) {
        drawer.open = true;
        target.scrollIntoView({ block: 'nearest' });
      }
    }
    window.addEventListener('hashchange', revealLinkedStop);

    function renderTentative(cities) {
      if (!tentativeEl || !tentativeList) return;
      var usable = cities.filter(function (city) {
        return city && typeof city.title === 'string' && city.title.trim();
      });
      tentativeList.innerHTML = usable.map(function (city) {
        var targetWindow = typeof city.targetWindow === 'string' && city.targetWindow.trim() ? city.targetWindow.trim() : 'Date TBD';
        return '<tr><td>' + esc(targetWindow) + '</td><th scope="row">' + esc(city.title) + '</th><td><span>Tentative</span></td></tr>';
      }).join('');
      tentativeEl.hidden = !usable.length;
    }

    function showNearest() {
      if (!nearestPoint) return;
      var candidates = allStops.filter(function (stop) {
        return stopStatus(stop) !== 'completed'
          && stop.lat != null
          && stop.lon != null
          && String(stop.lat).trim() !== ''
          && String(stop.lon).trim() !== ''
          && Number.isFinite(Number(stop.lat))
          && Number.isFinite(Number(stop.lon));
      }).map(function (stop) {
        return { stop: stop, miles: milesBetween(nearestPoint, { lat: stop.lat, lon: stop.lon }) };
      }).sort(function (a, b) { return a.miles - b.miles; }).slice(0, 5);

      var distances = {};
      candidates.forEach(function (candidate) {
        var amount = nearestPoint.metric ? candidate.miles * 1.609344 : candidate.miles;
        distances[candidate.stop.id] = 'About ' + Math.round(amount) + (nearestPoint.metric ? ' km away' : ' miles away');
      });
      renderSchedule(candidates.map(function (candidate) { return candidate.stop; }), distances);

      if (finderStatus) {
        finderStatus.dataset.state = '';
        finderStatus.textContent = candidates.length
          ? 'Showing the ' + candidates.length + ' closest upcoming stops to ' + nearestPoint.label + '.'
          : 'No upcoming stops with mapped locations are available.';
      }
      if (finderReset) finderReset.hidden = false;
    }

    if (finder && finderInput && finderSubmit) {
      finder.addEventListener('submit', function (event) {
        event.preventDefault();
        var postalCode = finderInput.value.trim();
        var validPostalCode = /^\d{5}(?:-\d{4})?$/.test(postalCode)
          || /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(postalCode);
        finderInput.setCustomValidity(validPostalCode ? '' : 'Enter a 5-digit U.S. ZIP or full Canadian postal code.');
        if (!finderInput.checkValidity()) { finderInput.reportValidity(); return; }

        finderSubmit.disabled = true;
        if (finderStatus) {
          finderStatus.dataset.state = '';
          finderStatus.textContent = 'Finding the closest upcoming stops…';
        }

        postJson(apiBase, '/api/estimate', { address: postalCode }).then(function (result) {
          var lat = Number(result.latitude), lon = Number(result.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) throw new Error('We could not place that ZIP or postal code. Check it and try again.');
          nearestPoint = {
            lat: lat,
            lon: lon,
            label: postalCode.toUpperCase(),
            metric: /^[A-Za-z]\d[A-Za-z]/.test(postalCode)
          };
          showNearest();
        }).catch(function (error) {
          nearestPoint = null;
          renderSchedule(allStops);
          if (finderReset) finderReset.hidden = true;
          if (finderStatus) {
            finderStatus.dataset.state = 'error';
            finderStatus.textContent = error.message || 'We could not find that location. Try again.';
          }
        }).finally(function () { finderSubmit.disabled = false; });
      });

      finderInput.addEventListener('input', function () { finderInput.setCustomValidity(''); });

      if (finderReset) {
        finderReset.addEventListener('click', function () {
          nearestPoint = null;
          finder.reset();
          finderReset.hidden = true;
          if (finderStatus) { finderStatus.dataset.state = ''; finderStatus.textContent = ''; }
          renderSchedule(allStops);
        });
      }
    }

    function render(stops) {
      var t = todayStamp();
      var upcoming = stops.filter(function (s) { return stopStatus(s) !== 'completed'; });
      allStops = stops;
      if (nearestPoint) showNearest(); else renderSchedule(stops);

      if (rangeEl) rangeEl.textContent = stops[0].dateLabel + ' – ' + stops[stops.length - 1].dateLabel;
      if (countEl) countEl.textContent = stops.length + (stops.length === 1 ? ' stop' : ' stops');
      if (remainingEl) {
        remainingEl.textContent = !upcoming.length ? 'Tour complete'
          : upcoming.length === 1 ? '1 stop remaining'
          : upcoming.length + ' stops remaining';
      }

      // Which ways in are offered depends on the phase, and the phase depends
      // on the feed — so it is settled here rather than in Liquid.
      var started = stops.some(function (s2) { return stampOf(s2.date) <= t; });
      var show = function (name, on) {
        var el = root.querySelector('[data-x1-way="' + name + '"]');
        if (el) el.hidden = !on;
      };
      show('register', upcoming.length > 0);
      show('register-closed', upcoming.length === 0);
      show('content', started);

      renderMap(stops);
    }

    function unavailable(text) {
      if (scheduleEl) {
        scheduleEl.innerHTML = '<p class="x1__notice"><strong>Schedule unavailable</strong>' + esc(text) + '</p>';
      }
      if (tourLayer) tourLayer.clearLayers();
      [rangeEl, remainingEl, countEl].forEach(function (el) { if (el) el.textContent = '—'; });
    }

    function load() {
      fetch(apiBase + '/api/events', { headers: { accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('feed'); return r.json(); })
        .then(function (data) {
          if (!data || data.ok !== true || !Array.isArray(data.stops)) throw new Error('feed');
          if (Array.isArray(data.tentativeCities)) renderTentative(data.tentativeCities);
          var stops = data.stops.filter(validStop).sort(compareStopsChronologically);
          if (!stops.length) {
            unavailable('No confirmed stops are listed yet. Check back shortly.');
            return;
          }
          render(stops);
        })
        .catch(function () {
          unavailable('We could not load the schedule just now. Please try again shortly.');
        });
    }

    load();
    window.setInterval(load, 60000);
  }


  /* ==========================================================================
     Preview curtain. A curtain, not a lock — see snippets/x1-curtain.liquid.
     Only the salted SHA-256 of the password is in the page, so reading the
     source does not hand anyone the password to pass around.
     ========================================================================== */

  function initLock(lock) {
    var expected = lock.getAttribute('data-x1-lock');
    var root = lock.closest('.x1');
    var form = lock.querySelector('[data-x1-lock-form]');
    var input = lock.querySelector('[data-x1-lock-input]');
    var msg = lock.querySelector('[data-x1-lock-msg]');
    if (!expected || !root || !form) return;
    if (surveyReviewMode() && root.querySelector('[data-kind="post-ride"]')) { lock.remove(); return; }

    var KEY = 'x1-preview-unlocked';

    function digest(value) {
      // Must match the Liquid side: password + '::' + shop domain, SHA-256 hex.
      var salted = value + '::' + (lock.dataset.x1LockSalt || location.hostname);
      return crypto.subtle.digest('SHA-256', new TextEncoder().encode(salted))
        .then(function (buf) {
          return [].map.call(new Uint8Array(buf), function (b) {
            return b.toString(16).padStart(2, '0');
          }).join('');
        });
    }

    function open() {
      lock.remove();
      try { sessionStorage.setItem(KEY, expected); } catch (e) { /* private mode */ }
    }

    // Unlock once per session, so moving between the five pages does not mean
    // retyping the password at every QR code.
    try {
      if (sessionStorage.getItem(KEY) === expected) { open(); return; }
    } catch (e) { /* private mode: fall through to the prompt */ }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var value = (input.value || '').trim();
      if (!value) { msg.dataset.state = 'error'; msg.textContent = 'Enter the preview password.'; return; }

      digest(value).then(function (hex) {
        if (hex === expected) { open(); return; }
        msg.dataset.state = 'error';
        msg.textContent = 'That password is not right.';
        input.select();
      }).catch(function () {
        msg.dataset.state = 'error';
        msg.textContent = 'This browser cannot check the password. Try a different browser.';
      });
    });
  }

  document.querySelectorAll('[data-x1-root]').forEach(init);
  document.querySelectorAll('[data-x1-hub]').forEach(initHub);
  document.querySelectorAll('[data-x1-lock]').forEach(initLock);
})();
